import React, { useEffect } from 'react';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { Text } from '@uhg-abyss/web/ui/Text';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Link } from '@uhg-abyss/web/ui/Link';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { API } from '@src/api';
import { TMemberInfo } from '@repo/utils';
export type MemberProfileLinksPanelProps = {
  lastSdohNeedsQuizDate: string | undefined;
  showMspLisQuizLink: boolean;
  showSdohNeedsQuizLink: boolean;
  onClickCboSearchLink: () => void;
  onClickMspLisQuizLink: () => void;
  onClickSdohNeedsQuizLink: () => void;
  onClickPlanBenefitsLink: () => void;
  memberInContext: TMemberInfo;
};

const VerticalDivider = () => {
  return (
    <hr
      style={{
        height: '164px',
        border: '1px solid #dfdfdf',
        marginLeft: '16px',
        marginRight: '16px',
      }}
    />
  );
};

const PseudoLinkText = ({ children }) => {
  return (
    <Text color="$core.color.brand.60" fontWeight="medium">
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {children}
      </span>
    </Text>
  );
};
export const MemberProfileLinksPanel = ({
  lastSdohNeedsQuizDate,
  showMspLisQuizLink,
  showSdohNeedsQuizLink,
  onClickCboSearchLink,
  onClickMspLisQuizLink,
  onClickSdohNeedsQuizLink,
  onClickPlanBenefitsLink,
  memberInContext,
}: MemberProfileLinksPanelProps) => {
  const [showPlanBenefits, setShowPlanBenefits] =
    React.useState<boolean>(false);
  useEffect(() => {
    async function fetchBenefitPlan(): Promise<void> {
      if (
        memberInContext?.benefitPlanPlatform !== undefined &&
        memberInContext?.benefitPlanID !== undefined &&
        memberInContext?.benefitPlanPlatform !== null &&
        memberInContext?.benefitPlanID !== null
      ) {
        const resp = await API.getBenefitCheck(
          memberInContext?.benefitPlanID,
          memberInContext?.benefitPlanPlatform
        );
        if (
          resp !== null &&
          resp !== undefined &&
          'status' in resp &&
          resp.status === 'success' &&
          'data' in resp
        ) {
          setShowPlanBenefits(resp.data?.isSearchAvailable);
        }
      }
    }
    if (
      !memberInContext?.medSup &&
      memberInContext?.lineOfBusiness !== 'E&I' &&
      memberInContext?.lineOfBusiness !== 'IFP'
    ) {
      fetchBenefitPlan();
    }
  }, [memberInContext]);
  const screenedWithin18Mo: boolean =
    lastSdohNeedsQuizDate &&
    dayjs(lastSdohNeedsQuizDate).isSameOrAfter(dayjs().subtract(18, 'months'));
  return (
    <Layout.Group
      style={{
        width: '100%',
        borderRadius: '8px',
      }}
      alignLayout="right"
    >
      {!memberInContext?.medSup &&
        showPlanBenefits &&
        memberInContext?.lineOfBusiness !== 'IFP' &&
        memberInContext?.lineOfBusiness !== 'E&I' && (
          <React.Fragment>
            <Link
              isStandardAnchor
              onClick={onClickPlanBenefitsLink}
              data-cy="planBenefitsLink"
            >
              <Layout.Stack
                role="link"
                style={{
                  cursor: 'pointer',
                  minWidth: '128px',
                  width: '100%',
                  textAlign: 'center',
                }}
              >
                <IconBrand
                  id="magnifying_glass_1"
                  size={64}
                  variant="twotonelightcircle"
                  icon="magnifying_glass"
                  brand="uhc"
                />

                <PseudoLinkText>
                  Plan Benefits{' '}
                  <IconSymbol icon="arrow_forward" />
                </PseudoLinkText>

                <Text size="$sm">Check for Available Plan Benefits</Text>
              </Layout.Stack>
            </Link>
            <VerticalDivider />
          </React.Fragment>
        )}
      {/* CBO search link */}
      <Link isStandardAnchor onClick={onClickCboSearchLink}>
        <Layout.Stack
          role="link"
          style={{
            cursor: 'pointer',
            minWidth: '128px',
            width: '100%',
            textAlign: 'center',
          }}
        >
          <IconBrand
            id="magnifying_glass_2"
            size={64}
            variant="twotonelightcircle"
            icon="magnifying_glass"
            brand="uhc"
          />

          <PseudoLinkText>
            CBO Search
            <IconSymbol icon="arrow_forward" />
          </PseudoLinkText>

          <Text size="$sm">Search for community-based organizations</Text>
        </Layout.Stack>
      </Link>

      {/* SDOH screener link */}
      {showSdohNeedsQuizLink && (
        <React.Fragment>
          <VerticalDivider />
          <Link isStandardAnchor onClick={onClickSdohNeedsQuizLink}>
            <Layout.Stack
              style={{
                cursor: 'pointer',
                minWidth: '148px',
                width: '100%',
                textAlign: 'center',
              }}
            >
              <IconBrand
                id="magnifying_glass"
                size={64}
                variant="twotonelightcircle"
                icon="clipboard_checked"
                brand="uhc"
              />

              <PseudoLinkText>
                SDOH Screener{' '}
                <IconSymbol icon="arrow_forward" />
              </PseudoLinkText>
              <Text size="$sm" data-cy="last-screened-on-text">
                {screenedWithin18Mo
                  ? `Last screened on ${lastSdohNeedsQuizDate}`
                  : `Member has not been screened`}
              </Text>
            </Layout.Stack>
          </Link>
        </React.Fragment>
      )}

      {/* MSP/LIS screener link */}
      {showMspLisQuizLink && (
        <React.Fragment>
          <VerticalDivider />
          <Link isStandardAnchor onClick={onClickMspLisQuizLink}>
            <Layout.Stack
              role="link"
              style={{
                minWidth: '128px',
                width: '100%',
                textAlign: 'center',
              }}
            >
              <IconBrand
                size={64}
                variant="twotonelightcircle"
                icon="go_digital"
                brand="uhc"
              />

              <PseudoLinkText>
                {/* MSP/LIS quiz -> */}
                MSP/LIS quiz{' '}
                <IconSymbol icon="arrow_forward" />
              </PseudoLinkText>
              <Text size="$sm">Medicare Savings Plan / Low-Income Subsidy</Text>
            </Layout.Stack>
          </Link>
        </React.Fragment>
      )}
    </Layout.Group>
  );
};
